import { Link } from "react-router-dom";
import { ArrowRight, Target, Eye, Leaf, ShieldCheck, FlaskConical, Users } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import ImageWithFallback from "@/components/ImageWithFallback";
import { IMAGES } from "@/lib/brand";
import { useSeo } from "@/lib/useSeo";

const VALUES = [
  { title: "Integrated Thinking", desc: "Solutions built around whole-pond requirements, not isolated products.", icon: Target },
  { title: "Scientific Approach", desc: "Modern product development grounded in aquaculture science.", icon: FlaskConical },
  { title: "Farmer Focus", desc: "Practical guidance for farmers, dealers and professionals.", icon: Users },
  { title: "Responsible Culture", desc: "Designed for sustainable, responsible aquaculture practices.", icon: Leaf },
];

export default function About() {
  useSeo({
    title: "About BIOSYN | Advanced Aquaculture Solutions",
    description: "BIOSYN provides integrated aquaculture solutions—minerals, probiotics, gut health, nutrition and pond management—combining product technology with practical technical guidance.",
    image: IMAGES.aerialPonds,
  });

  return (
    <div>
      <section className="relative overflow-hidden" style={{ background: "#063B63" }}>
        <ImageWithFallback src={IMAGES.aerialPonds} alt="Aerial view of aquaculture ponds" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="container-x relative py-20 md:py-28">
          <p className="overline-label" style={{ color: "#16B8D4" }}>About BIOSYN</p>
          <h1 className="mt-3 font-heading text-4xl md:text-5xl lg:text-6xl font-extrabold text-white max-w-3xl leading-tight">
            Complete Solutions for Modern Shrimp &amp; Fish Farming
          </h1>
        </div>
      </section>

      <section className="section container-x">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="rounded-3xl overflow-hidden biosyn-card !p-0">
            <ImageWithFallback src={IMAGES.shrimpHarvest} alt="Healthy prawns harvested from a well-managed pond" className="w-full h-[380px] object-cover" />
          </div>
          <div>
            <SectionHeading overline="Who We Are" title="Integrated Solutions, Not Isolated Products" />
            <p className="mt-5 text-base leading-relaxed" style={{ color: "#4A6B82" }}>
              BIOSYN provides a comprehensive range of aquaculture products, mineral solutions, probiotics, gut-health
              products, nutritional supplements, growth-support solutions and pond-management products, backed by practical
              technical guidance.
            </p>
            <p className="mt-4 text-base leading-relaxed" style={{ color: "#4A6B82" }}>
              We aim to provide integrated aquaculture solutions rather than isolated products—combining product technology
              with practical application support for farmers, dealers and aquaculture professionals.
            </p>
            <Link to="/products" className="btn-primary mt-7" data-testid="about-view-products">Explore Our Products <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>

      <section className="section grain" style={{ background: "#FFFFFF" }}>
        <div className="container-x">
          <SectionHeading align="center" overline="What Drives Us" title="Our Core Values" />
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {VALUES.map((v) => (
              <div key={v.title} className="biosyn-card p-7">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: "rgba(22,184,212,0.12)" }}>
                  <v.icon className="w-6 h-6" style={{ color: "#087FBF" }} />
                </div>
                <h3 className="font-heading font-bold text-lg" style={{ color: "#063B63" }}>{v.title}</h3>
                <p className="mt-2 text-sm leading-relaxed" style={{ color: "#4A6B82" }}>{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section container-x">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="biosyn-card p-8">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: "rgba(63,174,53,0.12)" }}>
              <Target className="w-6 h-6" style={{ color: "#197A35" }} />
            </div>
            <h3 className="font-heading text-2xl font-bold" style={{ color: "#063B63" }}>Our Mission</h3>
            <p className="mt-3 text-base leading-relaxed" style={{ color: "#4A6B82" }}>
              To develop and supply practical, consistent and application-oriented solutions that help farmers manage
              changing pond and culture conditions for better aquaculture performance.
            </p>
          </div>
          <div className="biosyn-card p-8">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: "rgba(8,127,191,0.1)" }}>
              <Eye className="w-6 h-6" style={{ color: "#087FBF" }} />
            </div>
            <h3 className="font-heading text-2xl font-bold" style={{ color: "#063B63" }}>Our Vision</h3>
            <p className="mt-3 text-base leading-relaxed" style={{ color: "#4A6B82" }}>
              To be a trusted partner in modern intensive aquaculture—recognised for integrated solutions, technical support
              and responsible culture practices.
            </p>
          </div>
        </div>
      </section>

      <section className="section" style={{ background: "#F5FAFC" }}>
        <div className="container-x text-center">
          <ShieldCheck className="w-10 h-10 mx-auto mb-4" style={{ color: "#3FAE35" }} />
          <h2 className="font-heading text-2xl md:text-3xl font-extrabold" style={{ color: "#063B63" }}>Ready to talk to our technical team?</h2>
          <div className="mt-6"><Link to="/contact" className="btn-primary" data-testid="about-contact-cta">Talk to a Technical Expert <ArrowRight className="w-4 h-4" /></Link></div>
        </div>
      </section>
    </div>
  );
}
